import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormField } from 'components/FormField/FormField';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { FormikToggleField } from 'components/FormikToggleField/FormikToggleField';
import { TextInput } from 'components/TextInput/TextInput';
import { WarningBox } from 'components/WarningBox/WarningBox';
import LeaveDialogue from 'containers/LeaveDialogue';
import type { SaveEvent } from 'containers/LeaveDialogue/LeaveDialog';
import { getCodeList } from 'country-list';
import { type FormikErrors, useFormik } from 'formik';
import type { Dispatch, SetStateAction } from 'react';
import type { IpfCertificateModel } from './model/IpfCertificateModel';

const codeList = getCodeList();
const COUNTRY_OPTIONS = Object.keys(codeList).map((key) => {
  return {
    value: key.toUpperCase(),
    label: `${key.toUpperCase()} - ${codeList[key]}`,
  };
});

export interface IpfCertificateFormFields {
  organization: string;
  department: string;
  city: string;
  state: string;
  country: string;
  subject1: string;
  subject2: string;
  subject3: string;
  dnsShort: boolean;
}

interface IpfCertificateFormProps {
  onSubmit: (values: IpfCertificateFormFields) => Promise<void>;
  commonName: string;
  model: IpfCertificateModel;
  setTransitionUrl: Dispatch<SetStateAction<SaveEvent | undefined>>;
}

export const IpfCertificateForm = (props: IpfCertificateFormProps) => {
  const formik = useFormik<IpfCertificateFormFields>({
    initialValues: {
      organization: props.model.values.organization ?? '',
      department: props.model.values.department ?? '',
      city: props.model.values.city ?? '',
      state: props.model.values.state ?? '',
      country: props.model.values.country ?? '',
      subject1: props.model.values.subject1 ?? '',
      subject2: props.model.values.subject2 ?? '',
      subject3: props.model.values.subject3 ?? '',
      dnsShort: props.model.values.dnsShort ?? false,
    },
    validate: (values: IpfCertificateFormFields) => {
      const MAX_LENGTH = 64;
      const REQUIRED_ERROR = 'This field is required';
      const LENGTH_ERROR = `This field is too long (max ${MAX_LENGTH} characters)`;

      const validateRequiredField = (
        values: IpfCertificateFormFields,
        field: keyof IpfCertificateFormFields,
        errors: FormikErrors<IpfCertificateFormFields>,
      ) => {
        if (!values[field]) {
          errors[field] = REQUIRED_ERROR;
        }
      };

      const validateMaxStringLength = (
        values: IpfCertificateFormFields,
        field: Exclude<keyof IpfCertificateFormFields, 'dnsShort'>,
        errors: FormikErrors<IpfCertificateFormFields>,
      ) => {
        if (values[field]?.length > MAX_LENGTH) {
          errors[field] = LENGTH_ERROR;
        }
      };

      const errors: FormikErrors<IpfCertificateFormFields> = {};

      validateRequiredField(values, 'organization', errors);
      validateMaxStringLength(values, 'organization', errors);

      validateRequiredField(values, 'city', errors);
      validateMaxStringLength(values, 'city', errors);

      validateRequiredField(values, 'state', errors);
      validateMaxStringLength(values, 'state', errors);

      validateRequiredField(values, 'country', errors);
      validateMaxStringLength(values, 'country', errors);

      validateMaxStringLength(values, 'department', errors);

      validateMaxStringLength(values, 'subject1', errors);

      validateMaxStringLength(values, 'subject2', errors);

      validateMaxStringLength(values, 'subject3', errors);

      return errors;
    },
    onSubmit: async (values) => {
      await props.onSubmit(values);
      formik.resetForm();
    },
  });

  const handleLeaveDialogueSave = (args: SaveEvent) => {
    props.setTransitionUrl(args);

    formik.handleSubmit();
  };
  const handleLeaveDialogOpen = () => {
    formik.setTouched({
      organization: true,
      department: true,
      city: true,
      state: true,
      country: true,
      subject1: true,
      subject2: true,
      subject3: true,
      dnsShort: true,
    });
  };

  return (
    <div>
      <LeaveDialogue
        isPristine={!formik.dirty}
        onSave={handleLeaveDialogueSave}
        onOpen={handleLeaveDialogOpen}
        msg={{
          btnSave: 'Save & Leave',
          btnLeave: 'Leave',
          header: 'The form data is not saved',
          subHeader: 'Do you really want to leave?',
        }}
        isValid={formik.isValid}
        saving={formik.isSubmitting}
      />

      <form onSubmit={formik.handleSubmit} autoComplete='off'>
        <WarningBox
          description={
            'Creating new CSR will invalidate all previously signed certificates.'
          }
        />
        <div className='settings-discovery__btn-save-wrapper'>
          <Button
            variant='primary'
            renderAs='link'
            to='/settings/system/ipfabric-certificates-upload'
            accent='positive'
            className='mr-2'
          >
            Upload
          </Button>
          <Button type='submit' variant='outline' loading={formik.isSubmitting}>
            Generate
          </Button>
        </div>

        <Card>
          <div className='p text-level-5 font-semibold'>
            Create a CSR (Certificate Signing Request)
          </div>
          <fieldset disabled={formik.isSubmitting}>
            <div className='flex gap-4 mb-4'>
              <div className='flex-1'>
                <FormField label='Common Name (Server Name)'>
                  <TextInput value={props.commonName} disabled type='text' />
                </FormField>
                <span className='help-block'>
                  The fully qualified domain name that clients will use to reach
                  your server.
                </span>
              </div>
              <div className='flex-1'>
                <FormikTextField
                  name='organization'
                  label='Organization name'
                  formik={formik}
                  type='text'
                />
                <span className='help-block'>
                  The exact legal name of your organization. Example: "IP
                  Fabric, Inc." Less commonly, if you do not have a legal
                  registered organization name, you should enter your own full
                  name here.
                </span>
              </div>
            </div>
            <div className='flex gap-4 mb-4'>
              <div className='flex-1'>
                <FormikTextField
                  name='department'
                  label='Department'
                  formik={formik}
                  type='text'
                />
                <span className='help-block'>
                  Department within your organization which you want to appear
                  in the certificate. It will be listed in the certificate's
                  subject as Organizational Unit, or "ou."
                </span>
              </div>
              <div className='flex-1'>
                <FormikTextField
                  name='city'
                  label='City'
                  formik={formik}
                  type='text'
                />
                <span className='help-block'>
                  The city where your organization is legally located.
                </span>
              </div>
            </div>
            <div className='flex gap-4 mb-4'>
              <div className='flex-1'>
                <FormikTextField
                  name='state'
                  label='State / Province'
                  formik={formik}
                  type='text'
                />
                <span className='help-block'>
                  USA state name. If you're creating a CSR for a location
                  outside of the USA, you can enter anything. It will accept any
                  state name you type.
                </span>
              </div>
              <div className='flex-1'>
                <FormikSelectField
                  name='country'
                  label='Country'
                  formik={formik}
                  options={COUNTRY_OPTIONS}
                  disabled={formik.isSubmitting}
                  searchable
                />
                <span className='help-block'>Country Name</span>
              </div>
            </div>
            <div className='flex gap-4 mb-4'>
              <div className='flex-1'>
                <FormikTextField
                  name='subject1'
                  label='Subject Alternative Name 1 (DNS name only):'
                  formik={formik}
                  type='text'
                />
              </div>
              <div className='flex-1'>
                <FormikTextField
                  name='subject2'
                  label='Subject Alternative Name 2 (DNS name only):'
                  formik={formik}
                  type='text'
                />
              </div>
              <div className='flex-1'>
                <FormikTextField
                  name='subject3'
                  label='Subject Alternative Name 3 (DNS name only):'
                  formik={formik}
                  type='text'
                />
              </div>
            </div>
            <span className='help-block'>
              The Subject Alternative Name fields lets you specify additional
              host names (sites, IP addresses, common names, etc.) to be
              protected by a single SSL Certificate, such as a Multi-Domain
              (SAN) or Extend Validation Multi-Domain Certificate.
            </span>
            <FormikToggleField
              formik={formik}
              name='dnsShort'
              label=''
              messages={{
                on: 'Include DNS short name in CSR SAN',
                off: 'Include DNS short name in CSR SAN',
              }}
            />
          </fieldset>
        </Card>
      </form>
    </div>
  );
};
